import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  CheckCircle2, Clock, Gift, History, Loader2, Lock, Sparkles, Target,
  Trophy, Wallet, X,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import client from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import {
  WHEEL_RADIUS, labelFontSize, polarPoint, radialLabelLayout, segmentPath,
  shortLabel,
} from '../lib/wheelGeometry';
import './Wheel.css';

const SPIN_DURATION_MS = 5200;
// Sonucu göstermeden önce çark yavaşlayıp tam dursun.
const RESULT_DELAY_MS = SPIN_DURATION_MS + 250;
const FULL_TURNS = 6;
// Son çevirmeler yan kartta durur; uzun liste çarkın yanında okunmuyor.
const HISTORY_LIMIT = 6;

const money = (value) => new Intl.NumberFormat('tr-TR', {
  style: 'currency', currency: 'TRY', maximumFractionDigits: 2,
}).format(Number(value || 0));

// Dış halkadaki süs ışıkları dilim sayısından bağımsızdır; çark dönerken
// yerlerinde kalsınlar diye sabit katmanda çizilirler.
const BULB_COUNT = 18;
const RIM_RADIUS = WHEEL_RADIUS + 6.5;

/** Kalan süreyi saat/dakika/saniye kutularına böler.
 *
 * Geri sayım tek bir cümle yerine kutulara ayrılır: oyuncunun sorduğu şey
 * "ne kadar kaldı" değil "ne zaman çevirebilirim"dir ve rakam okunur olmalıdır.
 */
export const countdownParts = (milliseconds) => {
  const total = Math.max(0, Math.ceil(milliseconds / 1000));
  return [
    { name: 'SAAT', value: Math.floor(total / 3600) },
    { name: 'DAKİKA', value: Math.floor((total % 3600) / 60) },
    { name: 'SANİYE', value: total % 60 },
  ];
};

const pad = (value) => String(value).padStart(2, '0');

/** Geri sayım çubuğunun dolulık oranı (0–1).
 *
 * Pencere genişliği moda göre değişir: kayan modda panelde girilen saat,
 * günlük modda bir gün. Sınır tanımlı değilse çubuk hiç çizilmez — sabit bir
 * pencere uydurmak oyuncuya yanlış bir ilerleme gösterirdi.
 */
export const cooldownProgress = (state, remainingMs) => {
  const windowMs = state?.cooldown_mode === 'daily'
    ? 24 * 60 * 60 * 1000
    : Math.max(0, Number(state?.cooldown_hours || 0)) * 60 * 60 * 1000;
  if (!windowMs || remainingMs <= 0) return null;
  const passed = 1 - Math.min(1, remainingMs / windowMs);
  return Math.max(0, Math.min(1, passed));
};

// Katılım koşulu metni: pencere cümleye girer, çünkü "toplam 500 ₺" ile
// "bugün 500 ₺" oyuncu için aynı şey değildir.
const depositRuleText = (state) => {
  const amount = money(state?.min_deposit);
  if (state?.min_deposit_window === 'daily') {
    return `Bugün (00:00–23:59) en az ${amount} yatırım gerekir`;
  }
  if (state?.min_deposit_window === 'rolling' || state?.min_deposit_hours > 0) {
    return `Son ${state.min_deposit_hours} saat içinde en az ${amount} yatırım gerekir`;
  }
  return `En az ${amount} toplam yatırım gerekir`;
};

const cooldownRuleText = (state) => {
  if (state?.cooldown_mode === 'daily') {
    return 'Günde bir çevirme hakkı (gece 00:00’da yenilenir)';
  }
  if (Number(state?.cooldown_hours || 0) > 0) {
    return `Her ${state.cooldown_hours} saatte bir çevirme hakkı`;
  }
  return 'Çevirme sıklığında sınır yok';
};

const REWARD_TAGS = {
  cash: { label: 'Anında bakiye', className: 'wheel-tag--cash' },
  bonus: { label: 'Çevrimli bonus', className: 'wheel-tag--bonus' },
  freespin: { label: 'Freespin', className: 'wheel-tag--freespin' },
  none: { label: 'Boş dilim', className: 'wheel-tag--none' },
};

const historyDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('tr-TR', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  }).format(date);
};

/** Çevirme sonucunun penceresi.
 *
 * Ayrı bir bileşendir çünkü ödülün nasıl anlatıldığı (boş dilim, çevrimli
 * bonus, çevrimsiz nakit, freespin) sayfanın en kolay bozulan yeridir ve
 * doğrudan test edilebilmelidir.
 */
export const WheelResult = ({ result, palette = [], onClose, onGoPromotions }) => {
  const segment = result?.segment || {};
  const blank = segment.reward_type === 'none';
  const wager = Number(result?.wager_required || 0);

  const description = () => {
    if (blank) {
      return 'Çark boş dilimde durdu. Bir sonraki hakkınızda tekrar deneyin.';
    }
    if (segment.reward_type === 'freespin') {
      return `${result.spin_count} freespin hesabınıza tanımlandı.`;
    }
    if (wager > 0) {
      return `${money(segment.amount)} bonus bakiyenize eklendi.`;
    }
    return `${money(segment.amount)} çevrimsiz olarak bakiyenize eklendi.`;
  };

  return (
    <div className="wheel-modal" role="dialog" aria-modal="true" aria-label="Çevirme sonucu">
      <button
        type="button"
        aria-label="Kapat"
        className="wheel-modal__veil"
        onClick={onClose}
      />
      <div className={`wheel-modal__card${blank ? ' is-blank' : ''}`}>
        {!blank && (
          <div className="wheel-confetti" aria-hidden="true">
            {Array.from({ length: 16 }, (_, index) => (
              <i
                key={index}
                style={{
                  left: `${(index * 6.3 + 3) % 100}%`,
                  background: palette[index % Math.max(1, palette.length)] || '#fbbf24',
                  animationDelay: `${(index % 6) * 0.14}s`,
                }}
              />
            ))}
          </div>
        )}

        <button
          type="button"
          onClick={onClose}
          className="wheel-modal__close"
          aria-label="Kapat"
        >
          <X size={16} />
        </button>

        <div className="wheel-modal__icon">
          {blank ? <Target size={28} /> : <Trophy size={28} />}
        </div>

        <h2 className="wheel-modal__title">
          {blank ? 'Bu sefer olmadı' : 'Tebrikler!'}
        </h2>

        {!blank && (
          <p className="wheel-modal__prize gold-text">{segment.label}</p>
        )}

        <p className="wheel-modal__text">{description()}</p>

        {wager > 0 && (
          <div className="wheel-modal__meta">
            <Lock size={12} />
            {`Çevrim şartı: ${money(wager)}`}
          </div>
        )}

        <div className="wheel-modal__actions">
          {!blank && segment.reward_type !== 'cash' && (
            <button
              type="button"
              onClick={onGoPromotions}
              className="gold-btn py-3 rounded-xl text-sm"
            >
              {segment.reward_type === 'freespin'
                ? 'Freespin Haklarım'
                : 'Bonuslarım'}
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="py-3 rounded-xl font-bold text-sm bg-white/5 border border-white/10"
          >
            Tamam
          </button>
        </div>
      </div>
    </div>
  );
};

const Wheel = () => {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [highlight, setHighlight] = useState('');
  const [now, setNow] = useState(() => Date.now());
  const resultTimer = useRef(null);
  const navigate = useNavigate();
  const { user, setWalletBalances } = useAuth();
  const { toast } = useToast();
  const userId = user?.id;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await client.get('/wheel');
      setState(data);
      setLoadError('');
    } catch (error) {
      setLoadError(error.response?.data?.detail || 'Şans çarkı yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, []);

  // Geçmiş oturum gerektirir; ziyaretçide istek hiç atılmaz ve kart çizilmez.
  const loadHistory = useCallback(async () => {
    if (!userId) {
      setHistory([]);
      return;
    }
    try {
      const { data } = await client.get('/wheel/history', {
        params: { limit: HISTORY_LIMIT },
      });
      setHistory(data?.items || []);
    } catch {
      // Geçmiş bir süstür: alınamaması çarkı kullanılamaz hâle getirmemeli.
      setHistory([]);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load, userId]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  // Geri sayım ve "hakkın doğdu" geçişi için saniyelik tik.
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => () => clearTimeout(resultTimer.current), []);

  // Sonuç penceresi Esc ile de kapanır: kutlama ekranı oyuncuyu kilitlememeli.
  useEffect(() => {
    if (!result) return undefined;
    const onKey = (event) => { if (event.key === 'Escape') setResult(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [result]);

  const segments = useMemo(() => state?.segments || [], [state]);
  const palette = useMemo(
    () => segments.map((segment) => segment.color).filter(Boolean),
    [segments],
  );
  const nextSpinAt = state?.next_spin_at ? new Date(state.next_spin_at).getTime() : 0;
  const remaining = nextSpinAt - now;
  const cooldownPassed = state?.reason === 'cooldown' && remaining <= 0;
  const canSpin = Boolean(state?.can_spin || cooldownPassed) && !spinning;
  const counting = state?.reason === 'cooldown' && remaining > 0;
  const progress = counting ? cooldownProgress(state, remaining) : null;

  const spin = async () => {
    if (!user) {
      toast({
        title: 'Giriş gerekli',
        description: 'Çarkı çevirmek için üye girişi yapın',
      });
      return;
    }
    setSpinning(true);
    setResult(null);
    setHighlight('');
    try {
      const { data } = await client.post('/wheel/spin');
      const sweep = 360 / Math.max(1, segments.length);
      // Kazanan dilimin ibrenin altına gelmesi için gereken görünür açı.
      const desired = (360 - (data.index * sweep + sweep / 2)) % 360;
      const current = ((rotation % 360) + 360) % 360;
      const delta = (desired - current + 360) % 360;
      setRotation(rotation + 360 * FULL_TURNS + delta);
      setWalletBalances(data);
      resultTimer.current = setTimeout(() => {
        setResult(data);
        setSpinning(false);
        setState((previous) => (previous ? {
          ...previous,
          can_spin: false,
          // Sıklık sınırının olup olmadığı yanıttan okunur: günlük modda saat
          // alanı 0 olsa da bir sonraki hak vardır.
          reason: data.next_spin_at ? 'cooldown' : '',
          next_spin_at: data.next_spin_at,
        } : previous));
        loadHistory();
      }, RESULT_DELAY_MS);
    } catch (error) {
      setSpinning(false);
      toast({
        title: 'Çark çevrilemedi',
        description: error.response?.data?.detail || 'Beklenmeyen bir hata oluştu',
      });
      load();
    }
  };

  if (loading) {
    return (
      <main className="min-h-[70vh] grid place-items-center">
        <Loader2 className="animate-spin" size={30} style={{ color: 'var(--pusu-gold)' }} />
      </main>
    );
  }

  if (loadError) {
    return (
      <main className="min-h-[70vh] grid place-items-center px-4">
        <div className="pusu-panel p-8 text-center max-w-md">
          <p className="text-sm" style={{ color: 'var(--pusu-muted)' }}>{loadError}</p>
        </div>
      </main>
    );
  }

  if (!state?.enabled) {
    return (
      <main className="min-h-[70vh] grid place-items-center px-4">
        <section className="pusu-panel p-10 text-center max-w-md">
          <div className="wheel-modal__icon">
            <Sparkles size={26} />
          </div>
          <h1 className="text-xl font-black mb-2">Şans Çarkı şu anda kapalı</h1>
          <p className="text-sm" style={{ color: 'var(--pusu-muted)' }}>
            Çark yeniden açıldığında bu sayfadan çevirebilirsiniz.
          </p>
        </section>
      </main>
    );
  }

  const statusLine = () => {
    if (spinning) {
      return { text: 'Çark dönüyor…', tone: '' };
    }
    if (canSpin) {
      return { text: 'Çevirme hakkınız hazır', tone: 'is-ready' };
    }
    if (state.reason === 'login') {
      return { text: 'Çarkı çevirmek için giriş yapın', tone: 'is-blocked' };
    }
    if (counting) {
      return { text: 'Yeni çevirme hakkınız için geri sayım', tone: 'is-blocked' };
    }
    return {
      text: state.message || 'Çarkı şu anda çeviremezsiniz',
      tone: 'is-blocked',
    };
  };

  const status = statusLine();
  const hubDisabled = Boolean(user) && (!canSpin || segments.length === 0);
  const hubLabel = user ? (spinning ? '' : 'ÇEVİR') : 'GİRİŞ';

  const onHub = () => {
    if (!user) {
      navigate('/', { state: { auth: 'login' } });
      return;
    }
    spin();
  };

  return (
    <main className="wheel-page">
      <header className="wheel-hero">
        <span className="wheel-hero__eyebrow">
          <Sparkles size={12} /> Günlük Şans
        </span>
        <h1 className="wheel-hero__title gold-text">{state.title}</h1>
        {state.description && (
          <p className="wheel-hero__text">{state.description}</p>
        )}
      </header>

      <div className="wheel-layout">
        <section className="wheel-stage">
          <div className={`wheel-disc${spinning ? ' is-spinning' : ''}`}>
            {segments.length === 0 ? (
              <div className="absolute inset-0 grid place-items-center rounded-full border border-dashed border-white/10 text-center px-8">
                <p className="text-sm" style={{ color: 'var(--pusu-muted)' }}>
                  Çarkta henüz ödül tanımlanmamış.
                </p>
              </div>
            ) : (
              <>
                <div className="wheel-disc__halo" aria-hidden="true" />

                {/* Dönen katman: yalnızca dilimler. Çerçeve, ışıklar ve göbek
                    sabit üst katmanda durur ki dönerken kaymasınlar. */}
                <svg
                  viewBox="0 0 200 200"
                  className="wheel-disc__layer wheel-disc__slices"
                  style={{
                    transform: `rotate(${rotation}deg)`,
                    transition: spinning
                      ? `transform ${SPIN_DURATION_MS}ms cubic-bezier(0.16, 0.84, 0.22, 1)`
                      : 'none',
                  }}
                  role="img"
                  aria-label={`${state.title} çarkı, ${segments.length} dilim`}
                >
                  {segments.map((segment, index) => {
                    const sweep = 360 / segments.length;
                    const center = index * sweep + sweep / 2;
                    const [textX, textY] = polarPoint(center, WHEEL_RADIUS * 0.9);
                    const { angle, anchor } = radialLabelLayout(center);
                    const dimmed = Boolean(highlight) && highlight !== segment.id;
                    return (
                      <g
                        key={segment.id}
                        className={`wheel-slice${dimmed ? ' is-dimmed' : ''}`}
                      >
                        <path
                          d={segmentPath(index, segments.length)}
                          fill={segment.color}
                          stroke="rgba(2,6,23,0.45)"
                          strokeWidth="0.6"
                        />
                        <text
                          x={textX}
                          y={textY}
                          fill={segment.text_color}
                          fontSize={labelFontSize(segments.length)}
                          fontWeight="800"
                          textAnchor={anchor}
                          dominantBaseline="middle"
                          transform={`rotate(${angle}, ${textX}, ${textY})`}
                        >
                          {shortLabel(segment.label, segments.length)}
                        </text>
                      </g>
                    );
                  })}
                </svg>

                <svg
                  viewBox="0 0 200 200"
                  className="wheel-disc__layer wheel-disc__layer--frame"
                  aria-hidden="true"
                >
                  <defs>
                    {/* Kasnağın metalik parıltısı renkten değil **ışıktan**
                        türer: beyaz/siyah opaklık katmanı kasnağın kendi
                        rengine (tema vurgusu) bindirilir. Gradyanın içine renk
                        karıştırmak (`color-mix`) her tarayıcıda SVG stop'unda
                        çözülmüyor ve kasnak o kurulumlarda siyah kalırdı. */}
                    <linearGradient id="wheel-rim-sheen" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#ffffff" stopOpacity="0.6" />
                      <stop offset="40%" stopColor="#ffffff" stopOpacity="0.06" />
                      <stop offset="60%" stopColor="#000000" stopOpacity="0.08" />
                      <stop offset="100%" stopColor="#000000" stopOpacity="0.5" />
                    </linearGradient>
                    {/* Derinlik: dilimlerin dış kenarı koyulaşınca çark düz bir
                        daire değil hacimli bir disk gibi görünür. Radyal
                        simetrik olduğu için sabit katmanda durur. */}
                    <radialGradient id="wheel-depth" cx="50%" cy="50%" r="50%">
                      <stop offset="52%" stopColor="rgba(0,0,0,0)" />
                      <stop offset="100%" stopColor="rgba(0,0,0,0.38)" />
                    </radialGradient>
                    <radialGradient id="wheel-gloss" cx="50%" cy="8%" r="62%">
                      <stop offset="0%" stopColor="rgba(255,255,255,0.22)" />
                      <stop offset="60%" stopColor="rgba(255,255,255,0)" />
                    </radialGradient>
                  </defs>

                  <circle cx="100" cy="100" r={WHEEL_RADIUS} fill="url(#wheel-depth)" />
                  <circle cx="100" cy="100" r={WHEEL_RADIUS} fill="url(#wheel-gloss)" />

                  {/* Dış kasnak: önce tema vurgusu, üstüne parıltı katmanı */}
                  <circle
                    cx="100" cy="100" r={RIM_RADIUS}
                    fill="none" stroke="var(--pusu-gold)" strokeWidth="9"
                  />
                  <circle
                    cx="100" cy="100" r={RIM_RADIUS}
                    fill="none" stroke="url(#wheel-rim-sheen)" strokeWidth="9"
                  />
                  <circle
                    cx="100" cy="100" r={WHEEL_RADIUS + 1}
                    fill="none" stroke="rgba(2,6,23,0.55)" strokeWidth="1.2"
                  />
                  <circle
                    cx="100" cy="100" r={RIM_RADIUS + 4.6}
                    fill="none" stroke="rgba(2,6,23,0.45)" strokeWidth="1"
                  />

                  {/* Kasnak ışıkları */}
                  {Array.from({ length: BULB_COUNT }, (_, index) => {
                    const [x, y] = polarPoint(
                      (360 / BULB_COUNT) * index, RIM_RADIUS);
                    return (
                      <circle
                        key={`${x.toFixed(2)}-${y.toFixed(2)}`}
                        className="wheel-bulb"
                        cx={x} cy={y} r="2.2"
                        fill="#fffbeb"
                        style={{ animationDelay: `${(index % 6) * 0.12}s` }}
                      />
                    );
                  })}
                </svg>

                {/* İbre: çarkın tepesinde sabit durur, kazanan dilimi gösterir */}
                <svg
                  className="wheel-pointer"
                  viewBox="0 0 40 54"
                  aria-hidden="true"
                >
                  <path
                    d="M20 54 L5 16 A15 15 0 1 1 35 16 Z"
                    fill="var(--pusu-gold)"
                    stroke="rgba(2,6,23,0.85)"
                    strokeWidth="2.5"
                    strokeLinejoin="round"
                  />
                  <circle
                    cx="20" cy="15" r="5.5"
                    fill="rgba(255,255,255,0.85)"
                  />
                </svg>

                <button
                  type="button"
                  onClick={onHub}
                  disabled={hubDisabled}
                  className={`wheel-hub${canSpin && user ? ' is-ready' : ''}`}
                  aria-label={user ? 'Çarkı çevir' : 'Giriş yap'}
                >
                  {spinning ? (
                    <Loader2 size={20} className="animate-spin" />
                  ) : (
                    <>
                      <span className="wheel-hub__label">{hubLabel}</span>
                      <span className="wheel-hub__hint">
                        {user ? 'ŞANSINI DENE' : 'ÜYE OL'}
                      </span>
                    </>
                  )}
                </button>
              </>
            )}
          </div>

          <div className="wheel-status">
            <p className={`wheel-status__line ${status.tone}`}>
              {status.tone === 'is-ready' && <CheckCircle2 size={13} />}
              {counting && <Clock size={13} />}
              {status.text}
            </p>

            {counting && (
              <div className="wheel-countdown">
                <div className="wheel-countdown__units">
                  {countdownParts(remaining).map((part) => (
                    <div key={part.name} className="wheel-countdown__unit">
                      <span className="wheel-countdown__value">{pad(part.value)}</span>
                      <span className="wheel-countdown__name">{part.name}</span>
                    </div>
                  ))}
                </div>
                {progress !== null && (
                  <div
                    className="wheel-countdown__bar"
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={Math.round(progress * 100)}
                  >
                    <div
                      className="wheel-countdown__fill"
                      style={{ width: `${Math.round(progress * 100)}%` }}
                    />
                  </div>
                )}
              </div>
            )}

            {!user && (
              <button
                type="button"
                onClick={() => navigate('/', { state: { auth: 'login' } })}
                className="wheel-login-link"
              >
                Giriş yap
              </button>
            )}
          </div>
        </section>

        <aside className="wheel-side">
          <div className="pusu-panel p-5">
            <h2 className="wheel-card__title">
              <Gift size={15} style={{ color: 'var(--pusu-accent-light)' }} />
              <span>Katılım Koşulları</span>
            </h2>
            <div className="wheel-rules">
              <div className="wheel-rule">
                <span className="wheel-rule__icon"><Clock size={13} /></span>
                <span>{cooldownRuleText(state)}</span>
              </div>
              {state.min_deposit > 0 && (
                <div className="wheel-rule">
                  <span className="wheel-rule__icon"><Wallet size={13} /></span>
                  <span>{depositRuleText(state)}</span>
                </div>
              )}
              <div className="wheel-rule">
                <span className="wheel-rule__icon"><Lock size={13} /></span>
                <span>Kazanan dilim sunucu tarafında belirlenir</span>
              </div>
            </div>
          </div>

          <div className="pusu-panel p-5">
            <h2 className="wheel-card__title">
              <Trophy size={15} style={{ color: 'var(--pusu-accent-light)' }} />
              <span>Çarktaki Ödüller</span>
              <span>{`${segments.length} dilim`}</span>
            </h2>
            <div>
              {segments.map((segment) => {
                const tag = REWARD_TAGS[segment.reward_type] || REWARD_TAGS.cash;
                return (
                  <button
                    key={segment.id}
                    type="button"
                    className="wheel-prize"
                    onMouseEnter={() => !spinning && setHighlight(segment.id)}
                    onMouseLeave={() => setHighlight('')}
                    onFocus={() => !spinning && setHighlight(segment.id)}
                    onBlur={() => setHighlight('')}
                  >
                    <span
                      className="wheel-prize__dot"
                      style={{ background: segment.color }}
                    />
                    <span className="wheel-prize__label">{segment.label}</span>
                    <span className={`wheel-tag ${tag.className}`}>{tag.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {user && (
            <div className="pusu-panel p-5">
              <h2 className="wheel-card__title">
                <History size={15} style={{ color: 'var(--pusu-accent-light)' }} />
                <span>Son Çevirmelerim</span>
              </h2>
              {history.length === 0 ? (
                <p className="wheel-empty">
                  Henüz çark çevirmediniz. İlk hakkınız sizi bekliyor.
                </p>
              ) : (
                <div className="wheel-history">
                  {history.map((item) => (
                    <div key={item.id} className="wheel-history__row">
                      <span className="wheel-history__label">
                        {item.segment_label || 'Çevirme'}
                      </span>
                      <span className="wheel-history__date">
                        {historyDate(item.created_at)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </aside>
      </div>

      {result && (
        <WheelResult
          result={result}
          palette={palette}
          onClose={() => setResult(null)}
          onGoPromotions={() => {
            setResult(null);
            navigate('/promosyonlar');
          }}
        />
      )}
    </main>
  );
};

export default Wheel;
